import {
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  input,
} from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faBan,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'button[boelsButton], a[boelsAnchorButton]',
  templateUrl: './button.component.html',
  imports: [FaIconComponent, NgClass, RouterModule],
  styleUrls: [
    './button.component.scss',
    './variants/button.main.component.scss',
    './variants/button.primary-brand.component.scss',
    './variants/button.primary-black.component.scss',
    './variants/button.secondary.component.scss',
  ],
})
export class ButtonComponent {
  /**
   * Input (Optional): Used to determine the style of the button based on the purpose
   * main:            The main variant should be used for the main action of the entire website (hire something).
   *                  It should be used sparingly and only be used once on a page.
   *                  With the exception when the buttons contain the same action.
   *
   *                  Expression level: Loud
   *
   * primary-brand:   The primary-brand variant should be used for actions that are
   *                  important and need attention but not the main action of the page.
   *
   *                  Expression level: Noisy
   *
   * primary-black:   The primary-black variant should be used for action(s) that do not need additional attention or when a
   *                  multitude of actions are different from each other but share the same priority.
   *                  This button can be considered the Standard button.
   *
   *                  Expression level: Mid
   *
   * secondary:       The secondary variant should be that do NOT need any attention. It blends into the background where you might
   *                  not notice it until you need it. When placed together with a Main button a visual hierarchy is created and promotes the
   *                  most important (Main) to the forefront. This button can be considered a Ghost button.
   *
   *                  Expression level: Low
   */
  variant = input<'main' | 'primary-brand' | 'primary-black' | 'secondary'>(
    'secondary',
  );
  /**
   * Input (Optional): Used to display an icon inside the button when provided
   */
  icon = input<IconDefinition>();
  /**
   * Input (Optional): Used to determine the shape of the button
   */
  shape = input<'square' | 'pill'>('square');
  /**
   * Input (Optional): Used to determine the size of the button, main effect is the height
   */
  size = input<'md' | 'sm'>('md');
  /**
   * Input (Optional): Used to determine on what background the button is placed
   * base: -- when button is placed within a parent with the default background (white)  --
   * inverted: -- when button is placed within a parent with a dark background (black) --
   * brand: -- when button is placed within a parent with the brand background (orange) --
   */
  mode = input<'base' | 'inverted' | 'brand'>('base');

  /**
   * Input (Optional): Used to determine the action of the button
   * upward: -- when the button should move the user to the top of the page --
   * downward: -- when the button should move the user to the bottom of the page --
   * next: -- when the button should move the user to the next section of the flow --
   * previous: -- when the button should move the user to the previous section of the flow --
   */
  action = input<'upward' | 'downward' | 'next' | 'previous' | 'none'>('next');

  alwaysShowActionIcon = input<boolean>(false);

  isLoading = input<boolean>(false);
  isDisabled = input<boolean>(false);

  disabled = computed(() => {
    const val = this.isDisabled() || this.isLoading();
    if (val) {
      this.iconAction = this.isLoading() ? faSpinner : faBan;
    } else {
      this.determineActionIcon();
    }
    return val;
  });

  @HostBinding('attr.disabled')
  get isDisabledAttr() {
    return this.disabled() ? true : null;
  }

  /**
   * Icon: Used to determine the icon of the button not to be confused with the icon input
   * The iconAction is displayed on hover, see input action for more info
   */
  iconAction: IconDefinition | null = faArrowRight;

  // Injects
  #elementRef = inject(ElementRef);

  /**
   * Construct and apply class for button component
   */
  @HostBinding('class') get buttonClass() {
    const classes = [
      `-${this.variant()}`,
      `-${this.size()}`,
      `-${this.shape()}`,
      `-${this.mode()}`,
      `-${this.action()}`,
      this.isLoading() ? '-loading' : '',
      this.icon() ? '-has-icon' : '-no-icon',
      this.alwaysShowActionIcon() && this.action() !== 'none'
        ? '-show-action-icon'
        : '',
    ];

    return classes.filter(Boolean).join(' ');
  }

  determineActionIcon() {
    switch (this.action()) {
      case 'upward':
        this.iconAction = faArrowUp;
        break;
      case 'downward':
        this.iconAction = faArrowDown;
        break;
      case 'next':
        this.iconAction = faArrowRight;
        break;
      case 'previous':
        this.iconAction = faArrowLeft;
        break;
      case 'none':
        this.iconAction = null;
        break;
      default:
        this.iconAction = faArrowRight;
        break;
    }
  }
}
